import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Star } from 'lucide-react';
import * as Icons from 'lucide-react';
import { services, projects, testimonials, companyInfo, metroMaintenanceServices, clients } from '../mockData';
import ClientCarousel from '../components/ClientCarousel';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1499815022134-5a333f5a299c"
            alt="Profab fabrication work"
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-fade-in">
            Precision. Quality. Profab.
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-10 max-w-3xl mx-auto">
            Expert metal fabrication and structural steel solutions for your most demanding projects
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-4 bg-profab-blue text-white rounded-md font-semibold text-lg transition-all hover:bg-profab-blue-dark hover:shadow-2xl hover:-translate-y-1 inline-flex items-center justify-center">

              Get a Quote
              <ArrowRight className="ml-2" size={20} />
            </Link>
            <Link
              to="/projects"
              className="px-8 py-4 bg-white text-gray-900 rounded-md font-semibold text-lg transition-all hover:bg-gray-100 hover:shadow-2xl hover:-translate-y-1">

              View Projects
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Company Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Building Excellence Since 2007

              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Profab is a leading provider of precision metal fabrication and structural steel solutions. 
                With over two decades of experience, we deliver exceptional quality and craftsmanship on every project.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                From complex industrial installations to custom architectural metalwork, our team brings 
                expertise, innovation, and unwavering commitment to excellence.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center text-profab-blue font-semibold hover:text-profab-blue-dark transition-colors">

                Learn More About Us
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1745448797900-35d08e85e9db"
                alt="Professional welding craftsmanship"
                className="rounded-lg shadow-2xl" />

              <div className="absolute -bottom-6 -left-6 bg-profab-blue text-white p-6 rounded-lg shadow-xl">
                <div className="text-4xl font-bold">20+
                </div>
                <div className="text-sm">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {companyInfo.stats.map((stat, index) => <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-profab-blue mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive metal fabrication solutions tailored to your specific needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = Icons[service.icon] || Icons.Wrench;
              return (
                <div
                  key={service.id}
                  className="group bg-white border border-gray-200 rounded-lg p-6 transition-all hover:shadow-xl hover:-translate-y-1 cursor-pointer">

                  <div className="w-14 h-14 bg-profab-blue/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-profab-blue transition-colors">
                    <IconComponent className="text-profab-blue group-hover:text-white transition-colors" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <span className="text-profab-blue font-medium inline-flex items-center group-hover:translate-x-1 transition-transform">
                    Learn More
                    <ArrowRight className="ml-2" size={16} />
                  </span>
                </div>);

            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center px-8 py-4 bg-profab-blue text-white rounded-md font-semibold transition-all hover:bg-profab-blue-dark hover:shadow-lg hover:-translate-y-1">

              View All Services
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Metro Station Maintenance Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-profab-blue/10 rounded-full mb-4">
              <span className="text-profab-blue font-semibold text-sm">SPECIALIZED SERVICES</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Civil Maintenance of Metro Stations
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Expert maintenance solutions ensuring the safety, functionality, and longevity of metro infrastructure
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {metroMaintenanceServices.map((service) => {
              const IconComponent = Icons[service.icon] || Icons.Wrench;
              return (
                <div
                  key={service.id}
                  className="group bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-lg p-8 transition-all hover:shadow-xl hover:border-profab-blue hover:-translate-y-1">

                  <div className="w-16 h-16 bg-profab-blue rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>);

            })}
          </div>

          <div className="mt-16 bg-profab-blue/5 border-l-4 border-profab-blue rounded-lg p-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <Icons.Train className="text-profab-blue" size={32} />
              </div>
              <div className="ml-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Trusted Partner for Metro Infrastructure
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Our specialized team brings extensive experience in maintaining critical metro station 
                  infrastructure. From structural integrity to aesthetic upkeep, we ensure seamless 
                  operations and passenger safety through comprehensive preventive and corrective maintenance programs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Featured Projects</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A showcase of our precision fabrication and installation work
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.slice(0, 3).map((project) =>
            <div
              key={project.id}
              className="group bg-white rounded-lg overflow-hidden shadow-md transition-all hover:shadow-2xl hover:-translate-y-1 cursor-pointer">

                <div className="relative h-64 overflow-hidden">
                  <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />

                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="inline-block px-3 py-1 bg-profab-blue rounded-full text-sm font-medium mb-2">
                      {project.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{project.description}</p>
                  <div className="text-sm text-gray-500">
                    <span>{project.location}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/projects"
              className="inline-flex items-center px-8 py-4 bg-gray-900 text-white rounded-md font-semibold transition-all hover:bg-gray-800 hover:shadow-lg hover:-translate-y-1">

              View All Projects
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Our Clients Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Clients</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Trusted by leading organizations across industries
            </p>
          </div>

          <ClientCarousel clients={clients} />

          <div className="mt-12 text-center">
            <p className="text-gray-600 italic">
              ...and many more valued partners across commercial and industrial sectors
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-profab-blue">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Get in touch with our team today for a free consultation and quote
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-10 py-4 bg-white text-profab-blue rounded-md font-semibold text-lg transition-all hover:bg-gray-100 hover:shadow-2xl hover:-translate-y-1">

            Contact Us Now
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>);

};

export default Home;